import React, { useState, ChangeEvent, FormEvent } from 'react';
import Footer from '../Guest/sections/PageFooter';
import Banner from '../Guest/sections/PageBanner';
import PageHeader from '../Guest/sections/PageHeader';
import axios from 'axios';
import { REACT_APP_UPLOADS } from '../../constants';

type FormData = {
  fullName:    string;
  phoneNumber: string;
  email:       string;
  location:    string;
  involvement: string;
  crimeType:   string;
  otherCrime:  string;
  agency:      string[];
  description: string;
};

const crimeOptions: string[] = [
  'Wildlife Crime',
  'Forestry Crime',
  'Pollution',
  'Land Degradation',
  'Marine Crime',
  'Other',
];

const agencyOptions: string[] = [
  'NEMA',
  'KWS',
  'KFS',
  'Ministry of Environment and Climate Change',
  'Ministry of Mining, Blue Economy and Maritime Affairs',
  'County Government',
  'National Police Service',
];

/* ── Page hero ──────────────────────────────────────────────────────── */
function PageHero() {
  return (
    <div
      className="relative w-full overflow-hidden flex flex-col justify-end"
      style={{ height: '46vh', minHeight: 320, background: '#052e16' }}
    >
      <img
        src="/images/v1/img12.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover select-none"
        style={{ opacity: 0.28 }}
      />
      <div className="absolute inset-0 bg-black/35 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#052e16]/20 via-transparent to-[#052e16]/95 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pb-12">
        {/* Bold whistleblower badge */}
        <span className="inline-flex items-center gap-2.5 mb-5 px-4 py-2 rounded-full bg-green-500/15 border border-green-400/40 backdrop-blur-sm">
          <svg fill="none" viewBox="0 0 24 24" stroke="rgb(74,222,128)" strokeWidth={2} className="w-4 h-4 shrink-0">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <span className="text-green-300 text-xs tracking-[0.18em] uppercase font-bold" style={{ fontFamily: 'var(--font-body)' }}>
            Whistleblower Protected
          </span>
        </span>
        <h1
          className="text-5xl lg:text-6xl font-bold text-white leading-tight"
          style={{ fontFamily: 'var(--font-display)', textShadow: '0 2px 24px rgba(0,0,0,0.5)' }}
        >
          Report an <span className="text-green-400">Incident</span>
        </h1>
        <p
          className="mt-4 text-white/65 text-base max-w-xl leading-relaxed"
          style={{ fontFamily: 'var(--font-body)', textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
        >
          Your report is treated with full confidentiality. All information is encrypted and
          protected under our Whistleblower Policy. Anonymous submissions are welcome.
        </p>
      </div>
    </div>
  );
}

/* ── Bold privacy / data-protection banner (plain-language GDPR) ─────── */
function PrivacyPromiseBanner() {
  const points = [
    {
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      ),
      title: 'Report Anonymously',
      desc:  'You never have to give your name. Leave the personal details blank and we will still receive and act on your report.',
    },
    {
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      ),
      title: 'Encrypted & Secure',
      desc:  'Everything you send is encrypted in transit. Only our trained review team can ever see your report — no one else.',
    },
    {
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
      ),
      title: 'Never Sold or Shared',
      desc:  'We do not sell your information or share it with advertisers or third parties. Your data is used only to act on your report.',
    },
    {
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      ),
      title: 'You Stay in Control',
      desc:  'You have the right to see, correct, or delete the information you give us at any time. Just email us and we will action it.',
    },
  ];

  return (
    <section className="w-full bg-[#041208] border-y border-green-900/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">

        {/* Heading */}
        <div className="text-center mb-12 lg:mb-16">
          <span className="inline-flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-green-400" />
            <span className="text-green-400 text-xs tracking-[0.22em] uppercase font-bold" style={{ fontFamily: 'var(--font-body)' }}>
              Your Privacy, Guaranteed
            </span>
            <span className="h-px w-8 bg-green-400" />
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mx-auto"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Speak Up Safely. <span className="text-green-400">We&apos;ve Got Your Back.</span>
          </h2>
          <p
            className="mt-5 text-white/55 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Reporting a wildlife crime should never put you at risk. Here is exactly what we do —
            and never do — with the information you share.
          </p>
        </div>

        {/* Four bold promises */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {points.map((p) => (
            <div
              key={p.title}
              className="flex flex-col rounded-2xl p-7 bg-white/[0.03] border border-green-900/40 hover:border-green-400/30 hover:bg-white/[0.05] transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-green-500/12 border border-green-500/25 flex items-center justify-center mb-5">
                <svg fill="none" viewBox="0 0 24 24" stroke="rgb(74,222,128)" strokeWidth={1.8} className="w-6 h-6">
                  {p.icon}
                </svg>
              </div>
              <h3 className="text-white font-bold text-lg mb-2.5 leading-snug" style={{ fontFamily: 'var(--font-display)' }}>
                {p.title}
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
                {p.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Legal footnote — plain language */}
        <div className="mt-10 lg:mt-12 flex items-start gap-3 max-w-3xl mx-auto rounded-2xl px-6 py-5 bg-green-500/[0.06] border border-green-500/20">
          <svg fill="none" viewBox="0 0 24 24" stroke="rgb(74,222,128)" strokeWidth={1.8} className="w-5 h-5 shrink-0 mt-0.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l9-4 9 4v6c0 5-3.8 8.5-9 10-5.2-1.5-9-5-9-10V6z" />
          </svg>
          <p className="text-white/55 text-sm leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
            <span className="text-white/80 font-semibold">Backed by law.</span>{' '}
            Your data is handled in line with international data-protection standards (GDPR) and
            Kenya&apos;s Data Protection Act, 2019. That means you have a legal right to privacy,
            to access your information, and to have it erased. Questions? Email{' '}
            <a href="mailto:info@lapa.africa" className="text-green-400 font-semibold hover:underline">info@lapa.africa</a>.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ── Shared input styles ────────────────────────────────────────────── */
const fieldCls =
  'w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500 transition-all';

/* ── Section label ──────────────────────────────────────────────────── */
function SectionLabel({ step, title, subtitle }: { step: string; title: string; subtitle?: string }) {
  return (
    <div className="flex items-start gap-4 mb-6">
      <span
        className="shrink-0 w-8 h-8 rounded-lg bg-[#052e16] text-green-400 flex items-center justify-center text-xs font-bold"
        style={{ fontFamily: 'var(--font-body)' }}
      >
        {step}
      </span>
      <div>
        <h3 className="text-gray-900 font-bold text-base" style={{ fontFamily: 'var(--font-display)' }}>
          {title}
        </h3>
        {subtitle && (
          <p className="text-gray-400 text-xs mt-0.5" style={{ fontFamily: 'var(--font-body)' }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

/* ── Main form ──────────────────────────────────────────────────────── */
const ReportIncident: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    fullName:    '',
    phoneNumber: '',
    email:       '',
    location:    '',
    involvement: '',
    crimeType:   '',
    otherCrime:  '',
    agency:      [],
    description: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({
        ...prev,
        agency: checked ? [...prev.agency, value] : prev.agency.filter((a) => a !== value),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    axios.post(
      `${REACT_APP_UPLOADS}/api/v1/incident-reports/submit`,
      [{
        fullName:                 formData.fullName,
        phoneNumber:              formData.phoneNumber,
        emailAddress:             formData.email,
        location:                 formData.location,
        directlyInvolved:         false,
        crimeTypes:               [formData.crimeType],
        otherCrimeTypeDescription: formData.otherCrime,
        agencies:                 formData.agency,
        description:              formData.description,
      }],
    )
      .then((res) => { if (res.status === 200) setSubmitted(true); })
      .catch((_err) => { setError('Something went wrong. Please try again or contact us directly.'); })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <Banner />
      <PageHeader />
      <PageHero />
      <PrivacyPromiseBanner />

      <div className="w-full bg-[#fafaf7] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">

            {/* ── Left: privacy note + tips ── */}
            <div className="lg:col-span-1 flex flex-col gap-6">
              {/* Confidentiality card */}
              <div
                className="rounded-2xl p-6"
                style={{ background: '#052e16', border: '1px solid rgba(74,222,128,0.15)' }}
              >
                <div className="w-10 h-10 rounded-xl bg-green-500/15 border border-green-500/20 flex items-center justify-center mb-4">
                  <svg fill="none" viewBox="0 0 24 24" stroke="rgba(74,222,128,0.85)" strokeWidth={1.8} className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <p className="text-white font-semibold text-sm mb-2" style={{ fontFamily: 'var(--font-display)' }}>
                  Your Identity Is Protected
                </p>
                <p className="text-white/55 text-xs leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
                  All reports are end-to-end encrypted. You may remain anonymous. Providing
                  false information may result in legal action.
                </p>
              </div>

              {/* Tips */}
              <div className="rounded-2xl p-6 bg-white border border-gray-200">
                <p className="text-gray-900 font-semibold text-sm mb-4" style={{ fontFamily: 'var(--font-display)' }}>
                  Tips for a strong report
                </p>
                {[
                  'Be as specific about the location as possible',
                  'Include dates, times, and names if known',
                  'Describe the incident in your own words',
                  'Note any agencies or officials involved',
                  'Attach photos or videos if you have them',
                ].map((tip) => (
                  <div key={tip} className="flex items-start gap-2 mb-3 last:mb-0">
                    <svg fill="none" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth={2.5} className="shrink-0 w-4 h-4 mt-0.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-500 text-xs leading-snug" style={{ fontFamily: 'var(--font-body)' }}>{tip}</span>
                  </div>
                ))}
              </div>

              {/* Contact fallback */}
              <div className="rounded-2xl p-5 bg-white border border-gray-200">
                <p className="text-gray-500 text-xs leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
                  Prefer to report by email?{' '}
                  <a href="mailto:info@lapa.africa" className="text-green-700 font-semibold hover:underline">
                    info@lapa.africa
                  </a>
                </p>
              </div>
            </div>

            {/* ── Right: form ── */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="flex flex-col items-center justify-center gap-5 py-20 rounded-2xl border border-green-200 bg-white">
                  <div className="w-14 h-14 rounded-full bg-green-600 flex items-center justify-center">
                    <svg fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2.5} className="w-7 h-7">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-green-800 font-bold text-2xl" style={{ fontFamily: 'var(--font-display)' }}>
                    Report Submitted
                  </h2>
                  <p className="text-green-700/65 text-sm text-center max-w-sm" style={{ fontFamily: 'var(--font-body)' }}>
                    Thank you. Your report has been received and will be reviewed by our team. Your
                    confidentiality is fully protected.
                  </p>
                  <a
                    href="/"
                    className="mt-2 px-6 py-2.5 rounded-full bg-green-700 text-white text-sm font-semibold hover:bg-green-600 transition-all"
                    style={{ fontFamily: 'var(--font-body)' }}
                  >
                    Return Home
                  </a>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-10">

                  {/* Section 1 — Contact */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-7">
                    <SectionLabel step="01" title="Contact Information" subtitle="You may leave contact fields blank to remain anonymous." />
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="fullName" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>Full Name</label>
                        <input id="fullName" name="fullName" type="text" placeholder="Your name (optional)" value={formData.fullName} onChange={handleChange} className={fieldCls} />
                      </div>
                      <div>
                        <label htmlFor="phoneNumber" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>Phone Number</label>
                        <input id="phoneNumber" name="phoneNumber" type="tel" placeholder="+254 …" value={formData.phoneNumber} onChange={handleChange} className={fieldCls} />
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="email" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>Email Address</label>
                        <input id="email" name="email" type="email" placeholder="you@example.com" value={formData.email} onChange={handleChange} className={fieldCls} />
                      </div>
                    </div>
                  </div>

                  {/* Section 2 — Incident details */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-7">
                    <SectionLabel step="02" title="Incident Details" subtitle="Be as specific as possible — every detail helps." />
                    <div className="flex flex-col gap-4">
                      <div>
                        <label htmlFor="location" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>Location of the Incident <span className="text-red-400">*</span></label>
                        <textarea id="location" name="location" rows={2} placeholder="Village, road, park, coordinates…" value={formData.location} onChange={handleChange} required className={fieldCls} style={{ resize: 'none' }} />
                      </div>
                      <div>
                        <label htmlFor="involvement" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>Your Involvement <span className="text-red-400">*</span></label>
                        <textarea id="involvement" name="involvement" rows={2} placeholder="Were you directly involved? Does it affect you or your community?" value={formData.involvement} onChange={handleChange} required className={fieldCls} style={{ resize: 'none' }} />
                      </div>
                    </div>
                  </div>

                  {/* Section 3 — Crime type */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-7">
                    <SectionLabel step="03" title="Type of Environmental Crime" subtitle="Select the category that best describes the incident." />
                    <div className="grid sm:grid-cols-2 gap-3">
                      {crimeOptions.map((crime) => (
                        <label
                          key={crime}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${formData.crimeType === crime ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-green-300'}`}
                        >
                          <input
                            type="radio"
                            name="crimeType"
                            value={crime}
                            checked={formData.crimeType === crime}
                            onChange={handleChange}
                            required
                            className="accent-green-600"
                          />
                          <span className="text-sm text-gray-700" style={{ fontFamily: 'var(--font-body)' }}>{crime}</span>
                        </label>
                      ))}
                    </div>
                    {formData.crimeType === 'Other' && (
                      <div className="mt-4">
                        <label htmlFor="otherCrime" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>Please specify</label>
                        <input id="otherCrime" name="otherCrime" type="text" placeholder="Describe the crime type…" value={formData.otherCrime} onChange={handleChange} className={fieldCls} />
                      </div>
                    )}
                  </div>

                  {/* Section 4 — Agencies */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-7">
                    <SectionLabel step="04" title="Agency Involved" subtitle="Select all relevant agencies (optional)." />
                    <div className="grid sm:grid-cols-2 gap-3">
                      {agencyOptions.map((agency) => (
                        <label
                          key={agency}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${formData.agency.includes(agency) ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-green-300'}`}
                        >
                          <input
                            type="checkbox"
                            name="agency"
                            value={agency}
                            checked={formData.agency.includes(agency)}
                            onChange={handleChange}
                            className="accent-green-600"
                          />
                          <span className="text-sm text-gray-700" style={{ fontFamily: 'var(--font-body)' }}>{agency}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Section 5 — Description */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-7">
                    <SectionLabel step="05" title="Description of the Incident" subtitle="Give as much detail as possible." />
                    <label htmlFor="description" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>
                      Full description <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={6}
                      placeholder="Describe what you witnessed, when it happened, who was involved, and any other relevant details…"
                      value={formData.description}
                      onChange={handleChange}
                      required
                      className={fieldCls}
                      style={{ resize: 'vertical' }}
                    />
                  </div>

                  {/* Error */}
                  {error && (
                    <p className="text-red-600 text-sm text-center" style={{ fontFamily: 'var(--font-body)' }}>
                      {error}
                    </p>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 rounded-full bg-green-700 text-white font-semibold text-sm hover:bg-green-600 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    style={{ fontFamily: 'var(--font-body)' }}
                  >
                    {submitting ? 'Submitting…' : 'Submit Report →'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ReportIncident;
